// The Activity page's data as a live query, and the count the sidebar shows.
import { GUEST_FREE_WAIT_MS } from '@ims/core'
import type { ActivityRows, TableName } from '@ims/data'
import { useMemo } from 'react'
import { useLive, useNow, useSettings } from '../hooks.ts'
import {
  buildActivity,
  paymentQrChange,
  periodRange,
  type ActivityEvent,
  type ActivityPeriod,
} from './activity.ts'

const TABLES: TableName[] = [
  'orders',
  'order_items',
  'kot_tickets',
  'kot_items',
  'order_requests',
  'deliveries',
  'sales_invoices',
  'credit_notes',
  'invoice_prints',
  'payments',
  'payment_allocations',
  'goods_receipts',
  'stock_movements',
  'dining_tables',
]

/** What happened in a period, newest first; undefined while it loads. */
export function useActivity(
  period: ActivityPeriod,
  on = true,
): { events: ActivityEvent[]; rows: ActivityRows; from: string; to: string } | undefined {
  // The clock moves every half minute; the day it names changes once a day.
  const now = useNow()
  const day = new Date(now).setHours(0, 0, 0, 0)
  const { from, to } = useMemo(() => periodRange(period, new Date(day)), [period, day])
  const rows = useLive(
    (r) => (on && r.reports ? r.reports.activity(from, to) : Promise.resolve(null)),
    [from, to, on],
    TABLES,
  ).data
  const nowMs = now.getTime()
  const settings = useSettings()
  const qrAt = settings?.payment_qr_set_at
  const qrBy = settings?.payment_qr_set_by
  const qrSet = settings?.payment_qr
  const qrLabel = settings?.payment_qr_label
  return useMemo(() => {
    if (!rows) return undefined
    const paymentQr = paymentQrChange({
      payment_qr: qrSet ?? null,
      payment_qr_label: qrLabel ?? null,
      payment_qr_set_at: qrAt,
      payment_qr_set_by: qrBy,
    })
    return { events: buildActivity(rows, { from, to, nowMs, paymentQr }), rows, from, to }
  }, [rows, from, to, nowMs, qrAt, qrBy, qrSet, qrLabel])
}

/** Today's things worth a manager's eye, for the sidebar. Zero when `on` is false. */
export function useNeedsLookCount(on: boolean): number {
  // One counting query, not the page's whole read: this runs on every screen.
  const now = useNow()
  const day = new Date(now).setHours(0, 0, 0, 0)
  const { from, to } = useMemo(() => periodRange('today', new Date(day)), [day])
  // A request is missed once it has waited this long; a five-minute step keeps the key calm.
  const missedBefore = new Date(
    Math.floor((now.getTime() - GUEST_FREE_WAIT_MS) / 300_000) * 300_000,
  ).toISOString()
  return (
    useLive(
      (r) =>
        on && r.reports ? r.reports.needsLookCount(from, to, missedBefore) : Promise.resolve(0),
      [from, to, missedBefore, on],
      ['orders', 'order_items', 'kot_items', 'deliveries', 'sales_invoices', 'order_requests'],
    ).data ?? 0
  )
}
